"use client";
import { useState, useEffect, useRef } from "react";
import { useCartContext } from "@/contexts/CartProvider";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import type { MultiCartTestResult } from "@/monarch/rules/multiCart/types";
import CartIcon from "./CartIcon";
import CartPopup from "./CartPopup";
import WelcomeBackModal from "./WelcomeBackModal";

interface MultiCartProps {
  className?: string;
}

export default function MultiCart({ className }: MultiCartProps) {
  const [isOpen, setIsOpen] = useState(false);
  const cartRef = useRef<HTMLDivElement>(null);
  const {
    state: { cartData },
    shouldShowBadge,
    getTotalItems,
  } = useCartContext();

  const cartItemCount = getTotalItems();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        cartRef.current &&
        event.target &&
        !cartRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <MonarchDataConsumer<MultiCartTestResult>
      experimentKey="useMultiCart"
      render={(testResult) => {
        // If no test data or test is disabled, return null
        if (!testResult || !testResult.data?.enabled) {
          return null;
        }
        return (
          <div className="relative" ref={cartRef}>
            <CartIcon
              className={className}
              shouldShowBadge={shouldShowBadge}
              cartItemCount={cartItemCount}
              onClick={() => setIsOpen(!isOpen)}
              onMouseEnter={() => setIsOpen(true)}
            />

            <CartPopup
              isOpen={isOpen}
              onMouseEnter={() => setIsOpen(true)}
              onMouseLeave={() => setIsOpen(false)}
              cartData={cartData}
              cartItemCount={cartItemCount}
            />
          </div>
        );
      }}
    />
  );
}
